import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Container,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { imageSrc } from '../../blog/blogApi';
import { PROVISION_GROUPS, formatLongDate } from '../../blog/blogForm';

function hasText(value) {
  return typeof value === 'string' && value.trim() !== '';
}

function Prose({ children }) {
  if (!hasText(children)) {
    return (
      <Text color="gray.400" fontSize="sm">
        Not added yet
      </Text>
    );
  }
  return (
    <Text whiteSpace="pre-wrap" color="gray.700" lineHeight="1.8">
      {children}
    </Text>
  );
}

function Block({ title, children }) {
  return (
    <Box mb={8}>
      <Heading as="h2" size="md" color="gray.800" mb={3} fontWeight="700">
        {title}
      </Heading>
      {children}
    </Box>
  );
}

function CaseStudyPreview({ blog, notice }) {
  const src = imageSrc(blog);
  const facts = Array.isArray(blog.facts) ? blog.facts.filter((item) => item.description || item.event_date) : [];
  const issues = Array.isArray(blog.legal_issues) ? blog.legal_issues.filter((item) => hasText(item.issue_text)) : [];
  const provisions = Array.isArray(blog.provisions) ? blog.provisions : [];
  const related = Array.isArray(blog.related_cases) ? blog.related_cases.filter((item) => hasText(item.case_name)) : [];

  const judgment = [
    ['final_decision', 'Final Decision of the Court'],
    ['relief_granted', 'Relief Granted or Denied'],
    ['ratio_decidendi', 'Ratio Decidendi'],
    ['judicial_reasoning', 'Judicial Reasoning / Analysis'],
    ['interpretation_of_law', 'Interpretation of Law'],
    ['important_reasoning', 'Important Reasoning Used by the Judges'],
    ['precedents', 'Precedents Relied Upon or Distinguished'],
    ['impact', 'Impact of the Judgment'],
    ['conclusion', 'Conclusion'],
  ];

  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" boxShadow="md">
      {notice && (
        <Alert status="info" borderRadius="0">
          <AlertIcon />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      <Box position="relative" minH={{ base: '280px', md: '380px' }} bg="#0f172a" overflow="hidden">
        {src && (
          <Image
            src={src}
            alt={blog.image_alt || ''}
            position="absolute"
            inset={0}
            w="100%"
            h="100%"
            objectFit="cover"
          />
        )}
        <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(15,23,42,0.92), rgba(15,23,42,0.55))" />
        <Box position="relative" zIndex={1} px={{ base: 5, md: 10 }} py={{ base: 10, md: 14 }} color="white">
          <Badge colorScheme="blue" mb={4} px={3} py={1} borderRadius="full">
            {blog.category || 'Case study'}
          </Badge>
          <Heading size="2xl" fontWeight="700" lineHeight="1.15" maxW="4xl">
            {blog.title || 'Untitled case study'}
          </Heading>
          {hasText(blog.case_name) && (
            <Text mt={3} fontSize="xl" color="blue.100">
              {blog.case_name}
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mt={8} maxW="4xl">
            <Text fontSize="sm"><strong>Court:</strong> {blog.court || '—'}</Text>
            <Text fontSize="sm"><strong>Year:</strong> {blog.year_of_decision || '—'}</Text>
            <Text fontSize="sm"><strong>Citation:</strong> {blog.citation || '—'}</Text>
          </SimpleGrid>
        </Box>
      </Box>

      <Container maxW="5xl" py={{ base: 6, md: 10 }}>
        <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={{ base: 4, md: 6 }} mb={8}>
          <Text fontSize="sm" fontWeight="700" color="blue.700" mb={2} letterSpacing="0.04em">
            CASE SUMMARY
          </Text>
          <Prose>{blog.short_summary}</Prose>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={10}>
          {[
            ['Decision date', blog.decision_date ? formatLongDate(blog.decision_date) : '—'],
            ['Area of law', blog.category || '—'],
            ['Status', blog.status === 'published' ? 'Published' : 'Draft'],
            ['URL slug', blog.slug || '—'],
          ].map(([label, value]) => (
            <Box key={label} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
              <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase">
                {label}
              </Text>
              <Text mt={1} color="gray.800" fontWeight="600">
                {value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <Block title="Introduction"><Prose>{blog.introduction}</Prose></Block>
        <Block title="Brief Background of the Case"><Prose>{blog.brief_background}</Prose></Block>
        <Block title="Importance of the Case"><Prose>{blog.importance}</Prose></Block>
        <Block title="Relevant Facts"><Prose>{blog.relevant_facts}</Prose></Block>
        <Block title="Parties Involved"><Prose>{blog.parties_involved}</Prose></Block>
        <Block title="Events Leading to the Dispute"><Prose>{blog.events_leading}</Prose></Block>

        <Block title="Facts in Chronological Order">
          {facts.length === 0 ? (
            <Text color="gray.400" fontSize="sm">Not added yet</Text>
          ) : (
            <Box borderLeft="2px solid" borderColor="blue.200" ml={2} pl={6}>
              {facts.map((fact, index) => (
                <Box key={`${fact.event_date}-${index}`} position="relative" mb={6}>
                  <Box position="absolute" left="-31px" top="6px" w="12px" h="12px" bg="blue.500" borderRadius="full" />
                  <Text fontSize="sm" color="blue.700" fontWeight="700">
                    {fact.event_date ? formatLongDate(fact.event_date) : 'Date not specified'}
                  </Text>
                  <Text whiteSpace="pre-wrap" color="gray.700" mt={1}>
                    {fact.description}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
        </Block>

        <Block title="Legal Issues / Questions Before the Court">
          {issues.length === 0 ? (
            <Text color="gray.400" fontSize="sm">Not added yet</Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              {issues.map((issue, index) => (
                <Box key={index} p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontWeight="700" color="gray.800">
                    Issue {index + 1}
                  </Text>
                  <Text whiteSpace="pre-wrap" color="gray.700" mt={1}>
                    {issue.issue_text}
                  </Text>
                </Box>
              ))}
            </VStack>
          )}
        </Block>

        <Block title="Relevant Legal Provisions">
          {PROVISION_GROUPS.map((group) => {
            const items = provisions.filter((item) => item.provision_type === group.type && hasText(item.provision_text));
            return (
              <Box key={group.type} mb={4}>
                <Text fontWeight="700" color="gray.700" mb={2}>
                  {group.label}
                </Text>
                {items.length === 0 ? (
                  <Text color="gray.400" fontSize="sm">Not added yet</Text>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {items.map((item, index) => (
                      <Text key={index} whiteSpace="pre-wrap" color="gray.700">
                        {item.provision_text}
                      </Text>
                    ))}
                  </VStack>
                )}
              </Box>
            );
          })}
        </Block>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
          <Box p={5} border="1px solid" borderColor="gray.200" borderRadius="lg">
            <Heading as="h2" size="md" mb={3}>Arguments of the Petitioner / Appellant</Heading>
            <Prose>{blog.petitioner_arguments}</Prose>
          </Box>
          <Box p={5} border="1px solid" borderColor="gray.200" borderRadius="lg">
            <Heading as="h2" size="md" mb={3}>Arguments of the Respondent</Heading>
            <Prose>{blog.respondent_arguments}</Prose>
          </Box>
        </SimpleGrid>

        <Block title="Judgment / Decision">
          <VStack align="stretch" spacing={5}>
            {judgment.map(([key, label]) => (
              <Box
                key={key}
                p={key === 'final_decision' ? 5 : 0}
                bg={key === 'final_decision' ? 'blue.50' : 'transparent'}
                borderRadius="md"
                borderLeft={key === 'final_decision' ? '4px solid' : 'none'}
                borderColor="blue.500"
              >
                <Text fontWeight="700" color="gray.800" mb={2}>
                  {label}
                </Text>
                <Prose>{blog[key]}</Prose>
              </Box>
            ))}
          </VStack>
        </Block>

        <Block title="Related Case Laws and References">
          {related.length === 0 ? (
            <Text color="gray.400" fontSize="sm">Not added yet</Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              {related.map((item, index) => (
                <Box key={index} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                  <Text fontWeight="700" color="gray.800">{item.case_name}</Text>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    {[item.citation, item.court, item.year].filter(Boolean).join(' · ') || 'Citation not added'}
                  </Text>
                  {/^https?:\/\//i.test(item.reference_url || '') && (
                    <Link href={item.reference_url} isExternal color="blue.600" fontSize="sm" fontWeight="600">
                      Open reference
                    </Link>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </Block>

        <Box bg="gray.50" borderRadius="md" p={4} border="1px solid" borderColor="gray.200">
          <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} letterSpacing="0.04em">
            SEARCH PREVIEW
          </Text>
          <Text fontWeight="700" color="blue.700">
            {blog.meta_title || blog.title || 'Meta title not added'}
          </Text>
          <Text fontSize="sm" color="green.700" mt={1}>
            /blog/{blog.slug || 'url-slug'}
          </Text>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {blog.meta_description || 'Meta description not added'}
          </Text>
        </Box>
      </Container>
    </Box>
  );
}

export default CaseStudyPreview;
